import {
  IAdaptor,
  UnsubscribeFunction,
  ConnectOptions,
} from '@Web/adapters/IAdaptor';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { GlobalBus } from '@core/Bus/EventBus';
import { StompConfig } from '@Web/config/StompConfig';
import {
  CONNECTION_CONFIG,
  CONNECTION_FAILURE_REASON,
} from '@Web/config/ConnectionConfig';
import {
  applyReconnectJitter,
  computeReconnectBackoffMs,
  nextReconnectAttemptCount,
} from '@Web/config/connectionHealth';
import { logConnection } from '@core/gameengine/reconnect/connectionLog';
import { logCrashReconnect } from '@core/gameengine/reconnect/crashReconnectLog';

export class StompAdaptor implements IAdaptor {
  private client: Client;
  private cfg: StompConfig;
  private connected = false;
  private subscriptions: Map<string, Map<(p: any) => void, StompSubscription>> =
    new Map();

  private reconnectAttempts = 0;
  private reconnectStatusEmitted = false;
  private connectionGeneration = 1;
  private replacingClient = false;
  private intentionalDisconnect = false;
  private waitingForNetwork = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectPromise: Promise<void> | null = null;
  private connectPromise: Promise<void> | null = null;
  private connectWaiters: {
    resolve: () => void;
    reject: (err: Error) => void;
  } | null = null;
  private connectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private networkListenersBound = false;
  private lastConnectOptions?: ConnectOptions;
  private readonly onBrowserOnline = (): void => {
    this.handleBrowserOnline();
  };
  private readonly onBrowserOffline = (): void => {
    this.handleBrowserOffline();
  };

  constructor(cfg: StompConfig) {
    if (!cfg) {
      throw new Error(
        'StompAdaptor Error: A configuration object must be provided to the constructor.'
      );
    }

    this.cfg = cfg;
    this.client = this.createClient();
    this.bindNetworkListeners();
  }

  getConnectionGeneration(): number {
    return this.connectionGeneration;
  }

  isReconnectInProgress(): boolean {
    return this.reconnectPromise != null || this.reconnectTimer != null;
  }

  isWaitingForNetwork(): boolean {
    return this.waitingForNetwork || this.isBrowserOffline();
  }

  private isBrowserOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  private createClient(): Client {
    const client = new Client({
      brokerURL: this.cfg.url,
    });
    // Controller/adaptor own reconnect so each attempt can use a fresh client.
    client.reconnectDelay = 0;
    client.onWebSocketClose = event => {
      if (this.client !== client) {
        return;
      }
      this.handleWebSocketClose(event);
    };
    client.onWebSocketError = event => {
      if (this.client !== client) {
        return;
      }
      this.handleWebSocketError(event);
    };
    client.onStompError = frame => {
      if (this.client !== client) {
        return;
      }
      this.handleStompError(frame);
    };
    return client;
  }

  private handleConnected(): void {
    this.connected = true;
    this.waitingForNetwork = false;
    this.reconnectStatusEmitted = false;
    this.reconnectAttempts = 0;
    this.client.reconnectDelay = 0;
    logConnection('CONNECTED', {
      generation: this.connectionGeneration,
    });
    logCrashReconnect('connected', {
      generation: this.connectionGeneration,
    });
    GlobalBus.emit('socketio:status', 'connected');
  }

  private emitReconnecting(): void {
    if (this.reconnectStatusEmitted) {
      return;
    }
    this.reconnectStatusEmitted = true;
    logConnection('RECONNECTING');
    GlobalBus.emit('socketio:status', 'reconnecting');
  }

  private handleStompError(frame: {
    headers?: Record<string, string>;
    body?: string;
  }): void {
    console.error('STOMP: Broker reported error:', frame.headers?.['message']);
    console.error('Additional details:', frame.body);
    logConnection('STOMP_ERROR', frame.headers?.['message'] ?? 'unknown');
    this.emitReconnecting();
  }

  private handleWebSocketError(event: unknown): void {
    logConnection('SOCKET_ERROR', event);
    this.emitReconnecting();
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = null;
    }
  }

  private failPendingConnect(reason: string): void {
    this.clearConnectTimeout();
    const waiters = this.connectWaiters;
    this.connectWaiters = null;
    this.connectPromise = null;
    if (waiters) {
      logConnection('RECONNECT_FAILED', reason);
      logCrashReconnect('connect failed', reason);
      waiters.reject(new Error(reason));
    }
  }

  private resolvePendingConnect(): void {
    this.clearConnectTimeout();
    const waiters = this.connectWaiters;
    this.connectWaiters = null;
    this.connectPromise = null;
    waiters?.resolve();
  }

  private handleBrowserOffline(): void {
    if (this.intentionalDisconnect) {
      return;
    }
    logConnection('NETWORK_OFFLINE');
    this.waitingForNetwork = true;
    this.clearReconnectTimer();
    this.clearOnlineReconnectTimer();
    this.connected = false;
    this.failPendingConnect(CONNECTION_FAILURE_REASON.NETWORK_OFFLINE);
    this.emitReconnecting();
    this.invalidateClientWithoutReconnect();
  }

  private handleBrowserOnline(): void {
    logConnection('NETWORK_ONLINE');
    this.reconnectAttempts = 0;
    if (this.intentionalDisconnect) {
      return;
    }
    this.waitingForNetwork = false;
    if (this.connected) {
      return;
    }
    this.emitReconnecting();
    this.clearOnlineReconnectTimer();
    this.onlineReconnectTimer = setTimeout(() => {
      this.onlineReconnectTimer = null;
      if (
        this.intentionalDisconnect ||
        this.connected ||
        this.isBrowserOffline()
      ) {
        return;
      }
      void this.hardReconnect(this.lastConnectOptions);
    }, CONNECTION_CONFIG.network.onlineReconnectDelayMs);
  }

  private bindNetworkListeners(): void {
    if (typeof window === 'undefined' || this.networkListenersBound) {
      return;
    }
    // Only online/offline. Do NOT listen to navigator.connection "change" —
    // effectiveType/type flips fire during DevTools resize, device emulation,
    // and mobile browser chrome, and were hard-reconnecting healthy sockets.
    window.addEventListener('online', this.onBrowserOnline);
    window.addEventListener('offline', this.onBrowserOffline);
    this.networkListenersBound = true;
  }

  private unbindNetworkListeners(): void {
    if (typeof window === 'undefined' || !this.networkListenersBound) {
      return;
    }
    window.removeEventListener('online', this.onBrowserOnline);
    window.removeEventListener('offline', this.onBrowserOffline);
    this.networkListenersBound = false;
  }

  private async invalidateClientWithoutReconnect(): Promise<void> {
    this.replacingClient = true;
    this.clearSubscriptions(true);
    try {
      await Promise.race([
        this.client.deactivate(),
        new Promise<void>(resolve => {
          setTimeout(resolve, CONNECTION_CONFIG.connection.deactivateTimeoutMs);
        }),
      ]);
    } catch (err) {
      console.warn(
        '[StompAdaptor] deactivate failed while waiting for network:',
        err
      );
    } finally {
      this.replacingClient = false;
    }
  }

  private handleWebSocketClose(event: unknown): void {
    this.connected = false;
    this.failPendingConnect('WebSocket closed before STOMP connected');
    this.clearSubscriptions(false);

    if (this.replacingClient || this.intentionalDisconnect) {
      return;
    }

    logConnection('SOCKET_CLOSED', event);
    this.emitReconnecting();

    if (this.waitingForNetwork || this.isBrowserOffline()) {
      this.waitingForNetwork = true;
      return;
    }

    // In-flight hardReconnect will retry from its own catch after connect rejects.
    if (this.reconnectPromise) {
      return;
    }

    logCrashReconnect('stale connection detected', event);
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (
      this.reconnectPromise ||
      this.intentionalDisconnect ||
      this.waitingForNetwork ||
      this.isBrowserOffline()
    ) {
      return;
    }
    const { jitterMs } = CONNECTION_CONFIG.reconnect;
    // Keep retrying at max delay after the attempt cap. The refresh overlay
    // may already be visible; the game still only opens after a page reload.
    this.reconnectAttempts = nextReconnectAttemptCount(this.reconnectAttempts);
    const nextDelay = applyReconnectJitter(
      computeReconnectBackoffMs(this.reconnectAttempts),
      jitterMs
    );

    this.emitReconnecting();

    logConnection('RECONNECT_ATTEMPT', {
      attempt: this.reconnectAttempts,
      delayMs: nextDelay,
      generation: this.connectionGeneration,
    });
    logCrashReconnect('connecting', {
      attempt: this.reconnectAttempts,
      delayMs: nextDelay,
      generation: this.connectionGeneration,
    });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.waitingForNetwork || this.isBrowserOffline()) {
        return;
      }
      void this.hardReconnect(this.lastConnectOptions);
    }, nextDelay);
  }

  private applyConnectOptions(options?: ConnectOptions): void {
    const finalUrl = new URL(this.cfg.url);
    if (options?.queryParams) {
      for (const key in options.queryParams) {
        finalUrl.searchParams.append(key, String(options.queryParams[key]));
      }
    }
    this.client.brokerURL = finalUrl.toString();
    this.client.connectHeaders = options?.headers || {};
  }

  connect(options?: ConnectOptions): Promise<void> {
    if (options) {
      this.lastConnectOptions = options;
    }
    if (this.getConnected()) {
      return Promise.resolve();
    }
    if (this.isBrowserOffline()) {
      this.waitingForNetwork = true;
      this.emitReconnecting();
      return Promise.reject(
        new Error(CONNECTION_FAILURE_REASON.NETWORK_OFFLINE)
      );
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.applyConnectOptions(options ?? this.lastConnectOptions);
    this.intentionalDisconnect = false;
    this.bindNetworkListeners();

    this.connectPromise = new Promise((resolve, reject) => {
      this.connectWaiters = { resolve, reject };

      this.client.onConnect = () => {
        this.handleConnected();
        this.resolvePendingConnect();
      };

      this.client.onStompError = frame => {
        this.handleStompError(frame);
        this.failPendingConnect(
          `STOMP error: ${frame.headers?.['message'] ?? 'unknown'}`
        );
        // Broker ERROR frames do not always close the socket; schedule our
        // own retry so status is not stuck on "reconnecting" with no attempt.
        if (!this.intentionalDisconnect && !this.waitingForNetwork) {
          this.scheduleReconnect();
        }
      };

      this.connectTimeoutId = setTimeout(() => {
        logConnection('CONNECT_TIMEOUT');
        logCrashReconnect('connect timeout');
        this.failPendingConnect(CONNECTION_FAILURE_REASON.CONNECT_TIMEOUT);
        this.emitReconnecting();
        void this.client.deactivate();
      }, CONNECTION_CONFIG.connection.connectTimeoutMs);

      logConnection('CONNECTING', {
        generation: this.connectionGeneration,
      });
      logCrashReconnect('connecting', {
        generation: this.connectionGeneration,
      });
      this.client.activate();
    });

    return this.connectPromise;
  }

  /**
   * Stop background reconnect timers without emitting a status event.
   * Used when the session is marked `failed` and the UI asks for a refresh.
   */
  stopAutoReconnect(): void {
    this.intentionalDisconnect = true;
    this.clearReconnectTimer();
    this.clearOnlineReconnectTimer();
    this.reconnectPromise = null;
    this.clearConnectTimeout();
    this.failPendingConnect('auto-reconnect abandoned');
  }

  disconnect(): Promise<void> {
    this.stopAutoReconnect();
    this.waitingForNetwork = false;
    this.unbindNetworkListeners();
    GlobalBus.emit('socketio:status', 'disconnected');
    this.connected = false;
    this.reconnectStatusEmitted = false;
    this.reconnectAttempts = 0;
    this.clearSubscriptions(true);
    return this.client.deactivate();
  }

  /**
   * Abruptly closes the WebSocket so the adaptor's reconnect path can
   * replace the client. Prefer hardReconnect() when a fresh session is required.
   */
  public forceDisconnect(): void {
    logCrashReconnect('deactivating old STOMP connection');
    if (this.client.webSocket) {
      this.client.webSocket.close();
    }
  }

  async hardReconnect(options?: ConnectOptions): Promise<void> {
    if (options) {
      this.lastConnectOptions = options;
    }
    if (this.intentionalDisconnect) {
      return;
    }
    if (this.waitingForNetwork || this.isBrowserOffline()) {
      this.waitingForNetwork = true;
      this.emitReconnecting();
      return;
    }
    if (this.reconnectPromise) {
      return this.reconnectPromise;
    }

    this.reconnectPromise = this.replaceClientAndConnect(
      options ?? this.lastConnectOptions
    )
      .catch(err => {
        logConnection('RECONNECT_FAILED', err);
        logCrashReconnect('connect failed', err);
        throw err;
      })
      .finally(() => {
        this.reconnectPromise = null;
      });

    const pending = this.reconnectPromise;
    void pending.catch(() => {
      queueMicrotask(() => {
        if (
          !this.intentionalDisconnect &&
          !this.reconnectPromise &&
          !this.waitingForNetwork
        ) {
          this.scheduleReconnect();
        }
      });
    });
    return pending;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearOnlineReconnectTimer(): void {
    if (this.onlineReconnectTimer) {
      clearTimeout(this.onlineReconnectTimer);
      this.onlineReconnectTimer = null;
    }
  }

  private async replaceClientAndConnect(
    options?: ConnectOptions
  ): Promise<void> {
    this.replacingClient = true;
    this.clearReconnectTimer();
    this.failPendingConnect('replacing STOMP client');
    this.connectionGeneration += 1;
    logCrashReconnect('deactivating old STOMP connection', {
      generation: this.connectionGeneration,
    });
    this.clearSubscriptions(true);
    this.connected = false;
    this.client.reconnectDelay = 0;
    const clientToDeactivate = this.client;
    try {
      await Promise.race([
        clientToDeactivate.deactivate(),
        new Promise<void>(resolve => {
          setTimeout(resolve, CONNECTION_CONFIG.connection.deactivateTimeoutMs);
        }),
      ]);
    } catch (err) {
      console.warn(
        '[StompAdaptor] deactivate failed during hard reconnect:',
        err
      );
    }

    this.client = this.createClient();
    this.replacingClient = false;
    this.intentionalDisconnect = false;

    this.emitReconnecting();

    await this.connect(options);
  }

  private clearSubscriptions(unsubscribe: boolean): void {
    if (unsubscribe) {
      this.subscriptions.forEach(eventSubscriptions => {
        eventSubscriptions.forEach(subscription => {
          try {
            subscription.unsubscribe();
          } catch {
            // Old client may already be dead.
          }
        });
      });
    }
    this.subscriptions.clear();
  }

  getConnected(): boolean {
    return this.connected;
  }

  on(event: string, listener: (payload: any) => void): UnsubscribeFunction {
    if (!this.subscriptions.has(event)) {
      this.subscriptions.set(event, new Map());
    }

    const subscribedGeneration = this.connectionGeneration;
    const subscription = this.client.subscribe(event, (message: IMessage) => {
      if (this.connectionGeneration !== subscribedGeneration) {
        return;
      }
      const adaptor_received_ts = Date.now();
      try {
        const payload = JSON.parse(message.body);
        payload._meta = { adaptor_received_ts };
        if (
          event.includes('chat') ||
          payload?.type?.includes('CHAT') ||
          payload?.op?.includes('CHAT')
        ) {
          console.log(
            `[StompAdaptor] Chat-related message from ${event}:`,
            payload
          );
        }
        listener(payload);
      } catch (e) {
        console.error(`Failed to parse message from ${event}:`, e);
      }
    });

    this.subscriptions.get(event)!.set(listener, subscription);

    return () => this.off(event, listener);
  }

  off(event: string, listener?: (payload: any) => void): void {
    const eventSubscriptions = this.subscriptions.get(event);
    if (!eventSubscriptions) return;

    const unsubscribeSafe = (subscription: StompSubscription) => {
      try {
        subscription.unsubscribe();
      } catch {
        // Subscription may already be gone with the previous client.
      }
    };

    if (listener) {
      const subscription = eventSubscriptions.get(listener);
      if (subscription) {
        unsubscribeSafe(subscription);
        eventSubscriptions.delete(listener);
      }
    } else {
      eventSubscriptions.forEach(sub => unsubscribeSafe(sub));
      eventSubscriptions.clear();
    }
  }

  emit(event: string, payload: any): void {
    if (!this.connected) {
      throw new Error('STOMP emit failed: not connected');
    }
    this.client.publish({
      destination: `${this.cfg.prefixes.app}/${event}`,
      body: JSON.stringify(payload),
      headers: { 'content-type': 'application/json' },
    });
  }

  getAdapterInstance(): any {
    return this.client;
  }
}
